import { yupResolver } from "@hookform/resolvers";
import { useForm } from "react-hook-form";
import { useToasts } from "react-toast-notifications";
import { Button, Form, Modal } from "semantic-ui-react";
import * as yup from "yup";
import { ControlledInput } from "../../../../../../../../components/ControlledForm";
import { parsedPhoneNumberAsString } from "../../../../../../../../components/global_components/form_components/ControlledPhoneNumber";
import { buildReturnUrl } from "../../../../../../../auth/hooks/useAuthIssueAnonymousTokenByOTPInURL";
import { useAuthOTPSend } from "../../../../../../../auth/hooks/useAuthOTPSend";
import { buildIdentifierString } from "../../../../subscribers/utils/buildIdentifierString";
import { FocusScope } from "react-aria";
import { ToastStatuses } from "../../../../../../../../components/global_components/Toast";

export type OTPModalData = {
  identifier: string;
  type: "Email" | "Phone";
};

export type OTPMutation = {
  mutate: (params: { identifier: string; type: "Email" | "Phone"; otp: string }) => Promise<void>;
  isLoading: boolean;
};

export function VerifyOTPModal({
  data,
  onClose,
  verifyOTPMutation,
  onChangeIdentifier = () => {},
}: {
  data: OTPModalData | null;
  onClose: () => void;
  verifyOTPMutation: OTPMutation;
  onChangeIdentifier?: () => void;
}) {
  const { addToast } = useToasts();

  const { sendAuthOTP, remainingSeconds, isAuthOTPSendLoading } = useAuthOTPSend({
    onSuccess: (data) => {
      addToast(`New code sent to ${buildIdentifierString(data)}`, {
        appearance: ToastStatuses.Success,
        autoDismiss: true,
      });
    },
  });

  const { control, handleSubmit, errors } = useForm<{
    otp: string;
  }>({
    resolver: yupResolver(
      yup.object().shape({
        otp: yup.string().required("Code is required").length(6, "Code must be 6 digits"),
      }),
    ),
    defaultValues: {
      otp: "",
    },
  });

  if (!data) return null;

  const headerText = data.type === "Email" ? "Check Your Email for a Code" : "Check Your Text Messages for a Code";
  const formattedIdentifier = data.type === "Email" ? data.identifier : parsedPhoneNumberAsString(data.identifier);
  const changeButtonText = data.type === "Email" ? "Change Email Address" : "Change Phone Number";

  return (
    <Modal open={!!data} onClose={onClose} closeIcon size='tiny' aria-labelledby='verify-otp-modal-header'>
      <Modal.Header id='verify-otp-modal-header' as='h2'>
        {headerText}
      </Modal.Header>
      <FocusScope contain autoFocus restoreFocus>
        <Modal.Content>
          <p>
            We{"'"}ve sent a 6-digit code to <b>{formattedIdentifier}</b>.
          </p>

          <Form
            onSubmit={handleSubmit(({ otp }) => {
              verifyOTPMutation.mutate({
                identifier: data.identifier,
                type: data.type,
                otp,
              });
            })}
          >
            <Form.Field required error={!!errors.otp}>
              <ControlledInput
                control={control}
                errors={errors}
                name='otp'
                autoComplete='one-time-code'
                label='Code'
                transform={{
                  input: (value: string) => value?.replace(/\D/g, "").slice(0, 6),
                  output: (value: string | null) => value?.replace(/\D/g, "").slice(0, 6),
                }}
                inputClassName='tw-border-border-dark focus:tw-ring-2 tw-ring-blue'
              />
              <div className='tw-flex tw-justify-between'>
                <button
                  type='button'
                  className='tw-mt-1 tw-cursor-pointer tw-appearance-none tw-border-none tw-bg-transparent tw-p-0 tw-text-sm tw-font-bold tw-text-blue hover:tw-underline'
                  disabled={remainingSeconds > 0 || isAuthOTPSendLoading}
                  onClick={() =>
                    sendAuthOTP({
                      identifier: data.identifier,
                      type: data.type,
                      returnUrl: buildReturnUrl(data.type, data.identifier),
                    })
                  }
                >
                  Resend Code {remainingSeconds > 0 ? `(${remainingSeconds}s)` : ""}
                </button>
                <button
                  type='button'
                  className='tw-mt-1 tw-cursor-pointer tw-appearance-none tw-border-none tw-bg-transparent tw-p-0 tw-text-sm tw-font-bold tw-text-blue hover:tw-underline'
                  onClick={onChangeIdentifier}
                >
                  {changeButtonText}
                </button>
              </div>
            </Form.Field>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button secondary onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={verifyOTPMutation.isLoading}
            primary
            onClick={handleSubmit(({ otp }) => {
              verifyOTPMutation.mutate({
                identifier: data.identifier,
                type: data.type,
                otp,
              });
            })}
          >
            Verify
          </Button>
        </Modal.Actions>
      </FocusScope>
    </Modal>
  );
}
